import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { serializeComment } from "components/comments/utils";
import { BasicComment } from "../../../lib/generated/app-service-gql/graphql";
import { updateLeadQuery } from "../../../lib/hooks/api/leads/update_cache/updateLeadEntityCache";
import { useAssertCurrentUser } from "../../../lib/hooks/api/users/useCurrentUser";
import { CommentObjectType } from "../../../lib/types/graphQLEnums";
import { CommentDto } from "../../comments/CommentComponent";
import CommentsSidebar from "../../comments/CommentsSidebar";

type Props = {
  leadId: string;
  leadName: string;
  comments: CommentDto[];
};

export default function LeadCommentsTab({ leadId, leadName, comments }: Props) {
  const queryClient = useQueryClient();

  const currentUser = useAssertCurrentUser();

  const onCreateComment = useCallback(
    (newComment: BasicComment) =>
      updateLeadQuery(queryClient, leadId, (lead) => ({
        ...lead,
        comments: [serializeComment(newComment, currentUser), ...lead.comments],
      })),
    [currentUser, leadId, queryClient],
  );

  const onUpdateComment = useCallback(
    (updatedComment: BasicComment) =>
      updateLeadQuery(queryClient, leadId, (lead) => ({
        ...lead,
        comments: lead.comments.map((comment) =>
          comment.id === updatedComment.id
            ? serializeComment(updatedComment, currentUser)
            : comment,
        ),
      })),
    [currentUser, leadId, queryClient],
  );

  const onDeleteComment = useCallback(
    (deletedCommentId: string) =>
      updateLeadQuery(queryClient, leadId, (lead) => ({
        ...lead,
        comments: lead.comments.filter((comment) => comment.id !== deletedCommentId),
      })),
    [leadId, queryClient],
  );

  return (
    <CommentsSidebar
      objectId={leadId}
      objectType={CommentObjectType.Lead}
      objectName={leadName}
      comments={comments}
      onCreateComment={onCreateComment}
      onUpdateComment={onUpdateComment}
      onDeleteComment={onDeleteComment}
    />
  );
}
